import React, { useState, useEffect } from 'react';
import { fetchStaffingAlerts, addToPriorityQueue } from '../../utils/api';
import DataFreshness from './DataFreshness';
import AddToQueueButton from '../common/AddToQueueButton';
import ProblemsOnlyToggle from './ProblemsOnlyToggle';
import { Button } from '../ui/button';

function StaffingAlerts({ unitId, serviceLineId, level }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProblemsOnly, setShowProblemsOnly] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const alertsData = await fetchStaffingAlerts({ unitId, serviceLineId, level });
        setData(alertsData);
      } catch (error) {
        console.error('Error loading staffing alerts:', error);
      }
      setLoading(false);
    };

    loadData();
  }, [unitId, serviceLineId, level]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-600">No staffing alerts data available</p>
      </div>
    );
  }

  const activeAlerts = Array.isArray(data.activeAlerts) ? data.activeAlerts : [];
  const problemCount = (data.criticalAlerts || 0) + (data.upcomingRisks || 0);

  const handleAddToQueue = async (payload) => {
    try {
      await addToPriorityQueue({
        title: payload.title,
        description: payload.metric || '',
        category: 'Staffing',
        priority: payload.priority || 'medium',
        estimatedTime: 30,
        dueDate: new Date().toISOString(),
        type: 'task',
        ...payload
      });
    } catch (err) {
      console.error('Add to queue failed:', err);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="flex justify-end">
        <ProblemsOnlyToggle
          enabled={showProblemsOnly}
          onChange={setShowProblemsOnly}
          count={problemCount}
        />
      </div>
      {/* Summary Cards - Consistent style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-red-critical">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Critical Alerts</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.criticalAlerts || 0}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-red-critical">Immediate action required</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: 'Address critical staffing alerts', metric: 'Critical Alerts', priority: 'high' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-yellow-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Upcoming Risks</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.upcomingRisks || 0}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">Next 48-72 hours</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: 'Review upcoming staffing risks', metric: 'Upcoming Risks', priority: 'medium' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-yellow-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Float Requests</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.floatRequests?.pending || 0}</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">{data.floatRequests?.fulfilled || 0} fulfilled, {data.floatRequests?.failed || 0} failed</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: 'Process float requests', metric: 'Float Requests' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Coverage Status</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{data.currentCoverage || 0}%</div>
            <div className={`flex items-center gap-1 text-[13px] mb-4 ${(data.staffScheduled || 0) > 0 ? 'text-red-critical' : 'text-green-nn'}`}>
              {(data.staffScheduled || 0) > 0 ? `${data.staffScheduled} positions short` : 'Fully staffed'}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: 'Review coverage gaps', metric: 'Coverage Status', priority: (data.staffScheduled || 0) > 0 ? 'high' : 'medium' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Active Staffing Alerts */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-gray-900">Active Staffing Alerts</h3>
          <p className="text-sm text-gray-600">Risk signals and early warnings - manage detailed schedules in Insight</p>
        </div>

        {activeAlerts.length > 0 ? (
          <div className="space-y-4">
            {activeAlerts.map((alert) => (
              <div
                key={alert.id}
                className="bg-red-50 border-l-4 border-red-500 rounded-lg p-5 relative"
              >
                {/* CRITICAL Badge */}
                <div className="absolute top-5 right-5">
                  <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded">
                    CRITICAL
                  </span>
                </div>

                {/* Alert Icon */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>

                  <div className="flex-1">
                    {/* Title */}
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {alert.title || 'Staffing Alert'}
                    </h4>

                    {/* Timeframe */}
                    {alert.timeframe && (
                      <div className="text-sm text-gray-700 mb-3">{alert.timeframe}</div>
                    )}

                    {/* Description */}
                    <p className="text-sm text-gray-800 mb-3">{alert.description || 'No description available'}</p>

                    {/* Impact */}
                    {alert.impact && (
                      <div className="mb-3">
                        <div className="font-semibold text-gray-900 text-sm mb-1">Impact:</div>
                        <div className="text-sm text-gray-700">{alert.impact}</div>
                      </div>
                    )}

                    {/* Recommendation */}
                    {alert.recommendation && (
                      <div className="mb-4">
                        <div className="font-semibold text-gray-900 text-sm mb-1">Recommendation:</div>
                        <div className="text-sm text-gray-700">{alert.recommendation}</div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border flex flex-wrap gap-2 items-center">
                      <AddToQueueButton
                        title={alert.title || 'Coordinate staffing'}
                        description={alert.description}
                        category="Staffing"
                        priority="critical"
                        estimatedTime={30}
                        impact={alert.impact}
                        sourceData={alert}
                        buttonStyle="primary"
                      />
                      <Button variant="default" size="sm" type="button">
                        Request Float Pool
                      </Button>
                      <Button variant="default" size="sm" type="button">
                        Call Supervisor
                      </Button>
                      <Button variant="default" size="sm" type="button">
                        View Schedule in Insight
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Staffing Alerts</h3>
            <p className="text-gray-600">All shifts are adequately staffed</p>
          </div>
        )}
      </div>

      <DataFreshness
        lastUpdated="15 minutes ago"
        source="Insight Scheduling Platform"
        status="current"
      />
    </div>
  );
}

export default StaffingAlerts;
